import React, {useContext, useEffect, useRef, useState} from 'react';
import block from 'bem-cn-lite';
import {AppContext} from '../../app/context';
import {Navigate, useNavigate} from 'react-router-dom';
import {useForm} from 'react-hook-form';
import {
    Link,
    Modal,
    Text,
    TextInput,
    useToaster,
    Button as GButton,
    Button
} from '@gravity-ui/uikit';
import {CApi} from '@services/api';
import {ArrowShapeTurnUpLeft} from '@gravity-ui/icons';
import KeysPng from '@assets/keys.png';
import {useMediaQuery} from 'react-responsive';
import {ToastVisibilityContext} from '@app/Providers/CustomToasterProvider/ToastVisibilityContext';

import './RemindPass.scss';

const b = block('remind-pass-page');

interface RemindPassForm {
    login: string;
    email: string;
}

export const RemindPass: React.FC = () => {
    const appCtx = useContext(AppContext);
    const isSmallScreen = useMediaQuery({query: '(max-width: 1150px)'});

    if (appCtx.state.token) {
        return <Navigate to="/home" />;
    }

    const [done, setDone] = useState(false);
    const [email, setEmail] = useState('');

    const formContent = (
        <>{done ? <Done email={email} /> : <Init setDone={setDone} setEmail={setEmail} />}</>
    );

    if (isSmallScreen) {
        return (
            <div className={`${b()} ${b()}--fullscreen`}>
                <div className={b('fullscreen-container')} style={{gap: done ? 0 : 20}}>
                    {formContent}
                </div>
            </div>
        );
    }

    return (
        <Modal open keepMounted className={b()}>
            {formContent}
        </Modal>
    );
};

interface InitProps {
    setDone: (done: boolean) => void;
    setEmail: (email: string) => void;
}

const Init: React.FC<InitProps> = ({setDone, setEmail}) => {
    const isSmallScreen = useMediaQuery({query: '(max-width: 1150px)'});
    const appCtx = useContext(AppContext);
    const navigateTo = useNavigate();
    const [loading, setLoading] = useState(false);

    const {
        register,
        handleSubmit,
        formState: {errors},
        getValues,
        setError
    } = useForm<RemindPassForm>({defaultValues: {login: '', email: ''}});

    const [showToast, setShowToast] = useState(false);
    const {add, remove} = useToaster();
    const {setCurrentToastName, currentToastName} = useContext(ToastVisibilityContext);
    const toastNameRef = useRef<string | null>(null);
    const toastName = 'remind_pass_attempt';

    const onSubmit = () => {
        appCtx.emit('increment', {name: 'remind_pass_attempt'});

        setLoading(true);
        CApi()
            .resendPassword.resendPasswordCreate({
                ...getValues()
            })
            .then(() => {
                appCtx.emit('clear_cnt', {name: 'remind_pass_attempt'});
                setLoading(false);
                setEmail(getValues('email'));
                setDone(true);
            })
            .catch(() => {
                if (appCtx.state.counters['remind_pass_attempt'] >= 5) {
                    setShowToast(true);
                }

                setLoading(false);
                setError('login', {message: 'Неправильный логин или e-mail'});
                setError('email', {message: 'Неправильный логин или e-mail'});
            });
    };

    useEffect(() => {
        if (showToast && appCtx.state.counters['remind_pass_attempt'] >= 5) {
            setCurrentToastName(toastName);
            toastNameRef.current = toastName;

            add({
                name: toastName,
                title: 'Неверные данные',
                theme: 'info',
                content: (
                    <div className={b('toast-content')}>
                        <Text variant="body-2">
                            Вы более 5 раз ввели неверные данные. Свяжитесь с менеджером<br/>
                            +7(383)375-16-62
                        </Text>
                        <GButton size="xl" onClick={() => navigateTo('/')} view="normal-contrast">
                            На главную
                        </GButton>
                    </div>
                ),
                autoHiding: false,
                isClosable: true
            });
        }
    }, [showToast]);

    const updateToasterStyles = () => {
        const root = document.documentElement;

        if (currentToastName === toastName) {
            root.style.setProperty('--g-toaster-top', isSmallScreen ? '100px' : 'auto');
            root.style.setProperty('--g-toaster-left', isSmallScreen ? '50%' : 'auto');
            root.style.setProperty('--g-toaster-right', isSmallScreen ? 'auto' : '34px');
            root.style.setProperty(
                '--g-toaster-transform',
                isSmallScreen ? 'translate(-50%, -50%)' : 'none'
            );
            root.style.setProperty('--g-toaster-inset-block-end', isSmallScreen ? 'auto' : '0');
            root.style.setProperty('--g-toaster-visibility', 'visible');
            root.style.setProperty('--g-toaster-width', isSmallScreen ? '328px' : '400px');
            root.style.setProperty('--g-toaster-content-width', isSmallScreen ? '232px' : '304px');
        }
    };

    useEffect(() => {
        updateToasterStyles();
    }, [currentToastName]);

    // Удаляем тост при размонтировании/смене страницы
    useEffect(() => {
        const root = document.documentElement;

        return () => {
            if (toastNameRef.current) {
                root.style.setProperty('--g-toaster-visibility', 'hidden');
                root.style.setProperty('--g-toaster-width', '400px');
                root.style.setProperty('--g-toaster-content-width', '304px');
                root.style.setProperty('--g-toaster-left', '50%');
                root.style.setProperty('--g-toaster-right', 'auto');
                root.style.setProperty('--g-toaster-transform', 'translate(-50%, -50%)');
                root.style.setProperty('--g-toaster-inset-block-end', 'auto');
                remove(toastNameRef.current);
                toastNameRef.current = null;
            }
        };
    }, [remove]);

    return (
        <>
            <Text variant="body-2">
                <Link
                    view="normal"
                    href="#"
                    onClick={() => navigateTo('/login')}
                    className={b('link-back') + ' ' + b('like-old-link')}
                >
                    <ArrowShapeTurnUpLeft />
                    &nbsp;Назад
                </Link>
            </Text>
            <Text variant="header-2">Восстановление пароля</Text>
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
                <div className={b('input')}>
                    <Text variant="body-2">Логин</Text>
                    <TextInput
                        size="xl"
                        {...register('login', {
                            required: 'Обязательно'
                        })}
                        errorMessage={errors.login && errors.login.message}
                        errorPlacement="outside"
                        validationState={errors.login && 'invalid'}
                    />
                </div>
                <div className={b('input')}>
                    <Text variant="body-2">Email</Text>
                    <TextInput
                        type="email"
                        size="xl"
                        {...register('email', {
                            required: 'Обязательно',
                            pattern: {
                                value: new RegExp('^\\S+@\\S+\\.\\S+$'),
                                message: 'Некорректный email'
                            }
                        })}
                        errorMessage={errors.email && errors.email.message}
                        errorPlacement="outside"
                        validationState={errors.email && 'invalid'}
                    />
                </div>
                <div className={b('input')}>
                    <Button view="action" type="submit" size="xl" loading={loading}>
                        Отправить
                    </Button>
                </div>
            </form>
        </>
    );
};

interface DoneProps {
    email: string;
}

const Done: React.FC<DoneProps> = ({email}) => {
    const navigateTo = useNavigate();

    return (
        <>
            <Text variant="body-2">
                <img src={KeysPng} width="148px" alt={'keys'} />
            </Text>
            <Text variant="header-2" className={b('done-header')}>
                Отправили на почту
            </Text>
            <Text variant="body-2">
                Письмо на&nbsp;
                <Link href={'mailto:' + email} className={b('like-old-link')}>
                    {email}
                </Link>
                &nbsp;придет в течение 5 минут. Если письма нет, пожалуйста, обратитесь к вашему
                менеджеру.
            </Text>
            <Text variant="body-2" className={b('link-back-text')}>
                <Button size={'xl'} onClick={() => navigateTo('/login')} className={b('link-back')}>
                    <ArrowShapeTurnUpLeft />
                    &nbsp;&nbsp;&nbsp;&nbsp;К авторизации
                </Button>
            </Text>
        </>
    );
};
